'use client';

import React, { useState, useEffect } from 'react';
import { useTranslation } from '@/context/LanguageContext';

interface RektorSectionClientProps {
    name?: string;
    image?: string;
    positionIdn?: string;
    positionEn?: string;
    roleIdn?: string;
    roleEn?: string;
    titleIdn?: string;
    titleEn?: string;
    contentIdn?: string;
    contentEn?: string;
}

export default function RektorSectionClient({ name, image, positionIdn, positionEn, roleIdn, roleEn, titleIdn, titleEn, contentIdn, contentEn }: RektorSectionClientProps) {
    const { t, language } = useTranslation();
    const role = language === 'id' ? (roleIdn || 'REKTOR') : (roleEn || 'RECTOR');
    const siteName = process.env.NEXT_PUBLIC_SITE_NAME || 'Universitas Mulawarman';
    const siteNameEnglish = process.env.NEXT_PUBLIC_SITE_NAME_ENGLISH || 'Mulawarman University';
    const currentSiteName = language === 'en' ? siteNameEnglish : siteName;

    const [imgSrc, setImgSrc] = useState<string>(image || '/img/rektor.png');

    useEffect(() => {
        if (image) {
            setImgSrc(image);
        } else {
            setImgSrc('/img/rektor.png');
        }
    }, [image]);

    return (
        <section id="profil" className="py-24 bg-white overflow-hidden">
            <div className="container mx-auto px-6">
                <div className="flex flex-col lg:flex-row items-center gap-16">
                    <div className="lg:w-5/12 relative group" data-aos="fade-right">
                        <div className="absolute -inset-4 bg-gradient-to-br from-unmulYellow to-unmulGreen rounded-3xl opacity-30 group-hover:opacity-50 transition blur-lg"></div>
                        <img
                            src={imgSrc}
                            alt={name || "Pimpinan"}
                            className="relative rounded-2xl shadow-2xl w-full object-cover h-[500px]"
                            style={{ height: 'auto', maxHeight: '500px' }}
                            onError={() => setImgSrc('/img/rektor.png')}
                        />
                        <div className="absolute bottom-6 -right-6 bg-white p-6 rounded-xl shadow-xl border-l-4 border-unmulGreen max-w-xs hidden md:block">
                            <p className="font-bold text-lg text-gray-800">{name || ''}</p>
                            <p className="text-sm text-unmulGreen font-semibold">
                                {language === 'id' ? positionIdn : positionEn}
                            </p>
                        </div>
                    </div>

                    <div className="lg:w-7/12" data-aos="fade-left">
                        <div className="flex items-center gap-3 mb-6">
                            <div className="h-1 w-12 bg-unmulYellow"></div>
                            <span className="text-unmulGreen font-bold tracking-widest uppercase text-sm">
                                {language === 'id' ? `SAMBUTAN ${role}` : `${role}'S WELCOME`}
                            </span>
                        </div>
                        <h2 className="text-4xl md:text-5xl font-serif font-bold text-gray-900 leading-tight mb-8">
                            {language === 'id' ? (titleIdn || 'Sambutan Rektor') : (titleEn || 'Rector\'s Welcome')}
                        </h2>
                        <div className="space-y-6 text-gray-600 text-lg leading-relaxed">
                            {language === 'id' ? (
                                contentIdn ? (
                                    <div dangerouslySetInnerHTML={{ __html: contentIdn }} className="[&>p]:mb-4 [&>ul]:list-disc [&>ul]:ml-5 [&>ol]:list-decimal [&>ol]:ml-5" />
                                ) : (
                                    <>
                                        <p>{t('rektor.paragraph1', { siteName: currentSiteName })}</p>
                                        <p>{t('rektor.paragraph2')}</p>
                                    </>
                                )
                            ) : (
                                contentEn ? (
                                    <div dangerouslySetInnerHTML={{ __html: contentEn }} className="[&>p]:mb-4 [&>ul]:list-disc [&>ul]:ml-5 [&>ol]:list-decimal [&>ol]:ml-5" />
                                ) : (
                                    <>
                                        <p>{t('rektor.paragraph1', { siteName: currentSiteName })}</p>
                                        <p>{t('rektor.paragraph2')}</p>
                                    </>
                                )
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
